import { Badge, Divider, Group, PasswordInput, Text, TextInput, Title } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useQuery } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'

// Pending (unsaved) DoseSpot clinic credentials in the organization form. The
// clinic key is write-only: it is the OAuth client_secret, the token call's
// password and the SSO hash input all at once, so the middleware never returns
// it and it must never reach a browser once saved.
export interface DoseSpotClinicFormRow {
  clinicId: string
  clinicKey: string
}

// The slice of the host form this component reads and writes (same casting
// convention as OrganizationStripeSection).
export type DoseSpotHostForm = UseFormReturnType<{ doseSpotClinic: DoseSpotClinicFormRow }>

interface OrganizationDoseSpotSectionProps {
  organizationId?: string
  form: DoseSpotHostForm
}

export function OrganizationDoseSpotSection({
  organizationId,
  form,
}: OrganizationDoseSpotSectionProps) {
  const orpc = useOrpc()

  const { data: status } = useQuery(
    orpc.getOrganizationDoseSpotStatus.queryOptions({
      input: { organizationId: organizationId ?? '' },
      enabled: Boolean(organizationId),
    }),
  )

  const configured = status?.configured === true

  return (
    <>
      <Divider my="sm" />
      <Group justify="space-between" mb="xs">
        <Title order={4}>DoseSpot (ePrescribing)</Title>
        {/* There is no shared fallback clinic: an organization without a pair
            cannot prescribe at all, so an empty state is "off", not "default". */}
        {configured ? (
          <Badge color="green" variant="light">
            Prescribing enabled
          </Badge>
        ) : (
          <Badge color="gray" variant="light">
            Not set up
          </Badge>
        )}
      </Group>

      <Text size="xs" c="dimmed" mb="xs">
        {configured
          ? `Clinic id: ${status?.clinicId}. Enter a new id and key below to replace it.`
          : 'This organization cannot prescribe until a clinic id and key are saved here.'}
      </Text>

      <TextInput
        label="Clinic id"
        placeholder="Numeric DoseSpot clinic id"
        {...form.getInputProps('doseSpotClinic.clinicId')}
      />
      <PasswordInput
        label="Clinic key"
        autoComplete="new-password"
        {...form.getInputProps('doseSpotClinic.clinicKey')}
      />
      <Text size="xs" c="dimmed">
        Every organization needs its own pair — there is no shared fallback. The pair is stored
        server-side exactly as entered and <strong>is not checked against DoseSpot</strong>, so a
        typo surfaces later as an account error when someone clicks Prescribe. The clinic key is
        never shown again.
      </Text>
    </>
  )
}
